'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import {
  Breadcrumbs,
  FAQSection,
  Header,
  PageShowcasesProvider,
  SponsoredBannersProvider,
} from '@/components';
import BenefitsSection from '@/components/sections/BenefitsSection';
import HolyShitDealsSection from '@/components/sections/HolyShitDealsSection';
import ProductShowcaseSection from '@/components/sections/ProductShowcaseSection';
import ReviewsBrowseSection from '@/components/sections/ReviewsBrowseSection';
import SelectDeliveryMenuSection from '@/components/sections/SelectDeliveryMenuSection';
import { REVIEWS_PAGE_FAQ_SPONSORED_POSITION } from '@/lib/sponsored-positions';
import type { Store } from '@/lib/types/stores';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';

export default function ReviewsPage() {
  const [stores, setStores] = useState<Store[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/stores`, { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          if (data?.stores) setStores(data.stores);
        }
      } catch {
        // ignore
      }
    })();
  }, []);

  return (
    <SponsoredBannersProvider>
      <PageShowcasesProvider endpoint="/api/reviews-page/product-showcases">
        <div className="min-h-screen">
          <Header stores={stores} base={API_BASE} />

          {/* Match contact page: single vertical rhythm from main; sections as siblings (contact/page.tsx) */}
          <main className="home-page space-y-16 px-4 pb-16 sm:space-y-24 sm:px-8">
            <section className="relative mt-4 overflow-hidden sm:mt-6" aria-labelledby="reviews-hero-heading">
              <div className="mx-auto w-full">
                <div className="relative w-full overflow-hidden rounded-2xl bg-brand-100 sm:rounded-2xl">
                  <div className="pointer-events-none absolute inset-0 z-0">
                    <Image
                      src="/media/pages/hero-bg-image.webp"
                      alt=""
                      fill
                      priority
                      className="object-cover object-center opacity-[0.05]"
                      sizes="100vw"
                      aria-hidden
                    />
                  </div>
                  <div className="pointer-events-none absolute inset-0 z-[1] bg-brand-100/10" aria-hidden />

                  <div className="relative z-[2] flex flex-col gap-6 p-4 sm:gap-8 sm:p-6 lg:p-8">
                    <div className="min-w-0 flex-1">
                      <Breadcrumbs items={[{ label: 'Reviews' }]} tone="onDark" />

                      <h1
                        id="reviews-hero-heading"
                        className="text-h2-mobile font-bold leading-tight text-white lg:text-h2-desktop"
                      >
                        What Our Customers Say
                      </h1>

                      <p className="mt-4 max-w-[52rem] text-body-16-desktop font-normal leading-relaxed text-white/90">
                        Don&apos;t just take our word for it. Read honest feedback from the Dank community about their
                        experiences in our stores. From product quality to our budtenders&apos; service, see why we&apos;re
                        Calgary&apos;s favorite dispensary.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <ReviewsBrowseSection stores={stores} apiBase={API_BASE} />

            <section aria-label="Benefits of shopping with Dank">
              <div className="-mx-4 sm:-mx-8">
                <BenefitsSection />
              </div>
            </section>

            <section aria-labelledby="select-delivery-menu-heading">
              <SelectDeliveryMenuSection />
            </section>

            <section aria-label="Frequently asked questions">
              <div className="[&>section]:!px-0">
                <FAQSection
                  sponsoredPosition={REVIEWS_PAGE_FAQ_SPONSORED_POSITION}
                  showSponsored
                />
              </div>
            </section>

            <section aria-label="Deals and promotions">
              <div className="[&>section]:!px-0">
                <HolyShitDealsSection />
              </div>
            </section>

            <section aria-label="Featured products">
              <ProductShowcaseSection slotKey="afterFAQ" className="!px-0 sm:!px-0 lg:!px-0" />
            </section>
          </main>
        </div>
      </PageShowcasesProvider>
    </SponsoredBannersProvider>
  );
}
